import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';

export interface BulkActionFooterProps {
  selectedCount: number;
  onCancelApprove: () => void;
  onCancelReject: () => void;
  onClearSelection: () => void;
  /** QA-371 — 일괄 배송완료(권한 있을 때만 전달). */
  onMarkDelivered?: () => void;
  /** QA-371 — 일괄 구매확정(권한 있을 때만 전달). */
  onConfirmPurchase?: () => void;
  /** QA-455 — 고른 주문의 주문내역서·거래명세서 일괄 출력. */
  onPrint?: (kind: 'order-statement' | 'transaction-statement') => void;
  /** QA-457 — 고른 주문 상태 일괄 변경(권한 있을 때만). */
  onStatusChange?: () => void;
  disabled?: boolean;
}

export const BulkActionFooter = ({
  selectedCount,
  onCancelApprove,
  onCancelReject,
  onClearSelection,
  onMarkDelivered,
  onConfirmPurchase,
  onPrint,
  onStatusChange,
  disabled,
}: BulkActionFooterProps) => {
  const { t } = useTranslation('orders');
  if (selectedCount === 0) return null;

  return (
    <div
      role="region"
      aria-label={t('orders.bulk.label')}
      className="sticky bottom-4 z-10 flex items-center gap-3 rounded-xl px-4 py-3 shadow"
      style={{ background: 'var(--surface-raised)' }}
      data-testid="orders-bulk-footer"
    >
      <span data-testid="orders-bulk-count" className="sr-only">
        {selectedCount}
      </span>
      <span className="text-sm text-[color:var(--content-strong)]">
        {t('orders.bulk.selected', { count: selectedCount })}
      </span>
      <Button
        type="button"
        variant="cta"
        size="sm"
        onClick={onCancelApprove}
        disabled={disabled}
        data-testid="orders-bulk-cancel-approve"
      >
        {t('orders.bulk.cancelApprove')}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onCancelReject}
        disabled={disabled}
        data-testid="orders-bulk-cancel-reject"
      >
        {t('orders.bulk.cancelReject')}
      </Button>
      {onMarkDelivered ? (
        <Button type="button" variant="outline" size="sm" onClick={onMarkDelivered} disabled={disabled} data-testid="orders-bulk-mark-delivered">
          {t('orders.bulk.markDelivered', '일괄 배송완료')}
        </Button>
      ) : null}
      {onConfirmPurchase ? (
        <Button type="button" variant="outline" size="sm" onClick={onConfirmPurchase} disabled={disabled} data-testid="orders-bulk-confirm-purchase">
          {t('orders.bulk.confirmPurchase', '일괄 구매확정')}
        </Button>
      ) : null}
      {onStatusChange ? (
        <Button type="button" variant="outline" size="sm" onClick={onStatusChange} disabled={disabled} data-testid="orders-bulk-status-change">
          {t('orders.bulk.statusChange', '주문상태 변경')}
        </Button>
      ) : null}
      {onPrint ? (
        <>
          <Button type="button" variant="outline" size="sm" onClick={() => onPrint('order-statement')} disabled={disabled} data-testid="orders-bulk-print-order-statement">
            {t('orders.bulk.printOrderStatement', '주문내역서 일괄 출력')}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => onPrint('transaction-statement')} disabled={disabled} data-testid="orders-bulk-print-transaction-statement">
            {t('orders.bulk.printTransactionStatement', '거래명세서 일괄 출력')}
          </Button>
        </>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        disabled={disabled}
        className="ml-auto"
      >
        {t('orders.bulk.clear')}
      </Button>
    </div>
  );
};
